import { DiceError, DiceExpression } from '../dice/dice.ts';
import { javaIsBlank } from '../javaText.ts';
import { SheetEvaluator } from './sheetEvaluator.ts';
import { FIELD_TYPES, ROLE_NAMES, SHEET_ROLES, allFields, isNumericType, isRollable, type SheetField, type SheetTemplate } from './sheetModel.ts';

/**
 * The mistakes a template can hold before any sheet uses it, as the desktop's
 * `sheets.SheetTemplateValidator` finds them and in its words: missing or repeated keys,
 * formulas and rolls that don't parse, references to fields that don't exist or aren't
 * numbers, derived fields that roll dice, loops, choices with no options, and roles on the
 * wrong kind of field.
 */

const KEY = /^[a-z][a-z0-9_]*$/;

export function templateProblems(template: SheetTemplate): string[] {
  const problems: string[] = [];
  const byKey = new Map<string, SheetField>();
  if (template.sections.length === 0) problems.push('The template has no sections yet.');
  for (const section of template.sections) {
    const where = section.title === '' ? 'An untitled section' : `"${section.title}"`;
    if (section.fields.length === 0) problems.push(`${where} has no fields.`);
    for (const field of section.fields) {
      if (!KEY.test(field.key)) {
        problems.push(`${field.label}: the key "${field.key}" should be lowercase letters, digits, and underscores, starting with a letter.`);
      } else if (byKey.has(field.key)) {
        problems.push(`Two fields use the key "${field.key}" (${byKey.get(field.key)!.label} and ${field.label}).`);
      } else {
        byKey.set(field.key, field);
      }
    }
  }

  for (const field of allFields(template)) {
    if (field.type === 'DERIVED' && field.formula === '') problems.push(`${field.label} is derived but has no formula.`);
    if (field.type === 'CHOICE' && field.options.length === 0) problems.push(`${field.label} is a choice with no options.`);
    if (field.formula !== '') checkExpression(field, 'formula', field.formula, byKey, field.type === 'DERIVED' || field.type === 'RESOURCE', problems);
    if (field.roll !== '') checkExpression(field, 'roll', field.roll, byKey, false, problems);
    if (field.type === 'DICE' && !javaIsBlank(field.defaultValue)) checkExpression(field, 'starting roll', field.defaultValue, byKey, false, problems);
  }

  // Loops only show up when the formulas are worked out.
  const evaluator = new SheetEvaluator(template, null);
  for (const field of allFields(template)) {
    if (field.type === 'DERIVED' || field.type === 'RESOURCE') {
      const problem = evaluator.problem(field.key);
      if (problem !== null && (problem.includes('loop') || problem.includes('depends on itself'))) problems.push(`${field.label}: ${problem}`);
    }
  }

  for (const role of SHEET_ROLES) {
    const key = template.roles[role];
    if (key === undefined) continue;
    const field = byKey.get(key);
    if (!field) {
      problems.push(`The ${ROLE_NAMES[role]} role points at "${key}", which isn't a field.`);
    } else if (role === 'HIT_POINTS' && field.type !== 'RESOURCE') {
      problems.push(`The Hit points role needs a resource field; ${field.label} is ${FIELD_TYPES[field.type].toLowerCase()}.`);
    } else if (role !== 'HIT_POINTS' && !isNumericType(field.type) && !isRollable(field)) {
      problems.push(`The ${ROLE_NAMES[role]} role needs a number or a roll; ${field.label} is text.`);
    }
  }
  return problems;
}

function checkExpression(field: SheetField, what: string, text: string, byKey: ReadonlyMap<string, SheetField>, mustBeFixed: boolean, problems: string[]): void {
  let expression: DiceExpression;
  try {
    expression = DiceExpression.parse(text);
  } catch (e) {
    if (!(e instanceof DiceError)) throw e;
    problems.push(`${field.label}'s ${what}: ${e.message}`);
    return;
  }
  if (mustBeFixed && expression.hasDice()) {
    problems.push(`${field.label}'s ${what} rolls dice; a ${FIELD_TYPES[field.type].toLowerCase()} field needs a fixed value. Put dice in a roll instead.`);
  }
  for (const reference of expression.references()) {
    let target = byKey.get(reference);
    if (!target && reference.endsWith('_max')) {
      const base = byKey.get(reference.slice(0, -4));
      if (base && base.type === 'RESOURCE') continue;
      target = undefined;
    }
    if (!target) problems.push(`${field.label}'s ${what} uses @${reference}, which isn't a field.`);
    else if (!isNumericType(target.type)) problems.push(`${field.label}'s ${what} uses @${reference}, but ${target.label} is ${FIELD_TYPES[target.type].toLowerCase()}, not a number.`);
  }
}
